'use client';
import { createClient } from '@/lib/supabase/client';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ShiftType = 'صباحية' | 'مسائية' | 'ليلية';
export type UserRoleLevel = 'مدخل_بيانات' | 'مشرف' | 'مدير_اداري' | 'مدير_تنفيذي';
export type MaterialUnit = 'كجم' | 'طن' | 'لتر' | 'متر' | 'قطعة' | 'صندوق' | 'كيس' | 'برميل';

export interface SecurityStaff {
  id: string;
  name: string;
  nationalId: string;
  mobile: string;
  idCardImageUrl: string;
  isActive: boolean;
  createdAt: string;
}

export interface SecurityShift {
  id: string;
  staffId: string;
  staffName?: string;
  shiftType: ShiftType;
  shiftDate: string;
  startTime: string;
  endTime: string;
  gate: string;
  notes: string;
  createdAt: string;
}

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: UserRoleLevel;
  department: string;
  isActive: boolean;
  avatarUrl: string;
  createdAt: string;
}

export interface RawMaterial {
  id: string;
  name: string;
  quantity: number;
  unit: MaterialUnit;
  imageUrl: string;
  addedDate: string;
  notes: string;
  createdAt: string;
}

export interface FinishedProduct {
  id: string;
  name: string;
  quantity: number;
  unit: MaterialUnit;
  imageUrl: string;
  productionDate: string;
  batchNumber: string;
  notes: string;
  createdAt: string;
}

// ─── Error helper ─────────────────────────────────────────────────────────────
function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error/i.test(error.message);
  }
  return false;
}

// ─── Image Upload ─────────────────────────────────────────────────────────────
export async function uploadImage(
  bucket: 'id-cards' | 'materials' | 'products',
  file: File,
  path: string
): Promise<string> {
  const supabase = createClient();
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, { upsert: true, cacheControl: '3600' });
  if (error) throw error;
  const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(data.path);
  return publicUrl;
}

// ─── Security Staff ───────────────────────────────────────────────────────────
export const securityStaffService = {
  async getAll(): Promise<SecurityStaff[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('security_staff')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (data || []).map((r) => ({
      id: r.id,
      name: r.name,
      nationalId: r.national_id,
      mobile: r.mobile,
      idCardImageUrl: r.id_card_image_url || '',
      isActive: r.is_active,
      createdAt: r.created_at,
    }));
  },

  async create(staff: Omit<SecurityStaff, 'id' | 'createdAt'>): Promise<SecurityStaff> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('security_staff')
      .insert({
        name: staff.name,
        national_id: staff.nationalId,
        mobile: staff.mobile,
        id_card_image_url: staff.idCardImageUrl,
        is_active: staff.isActive,
      })
      .select()
      .single();
    if (error) throw error;
    return {
      id: data.id,
      name: data.name,
      nationalId: data.national_id,
      mobile: data.mobile,
      idCardImageUrl: data.id_card_image_url || '',
      isActive: data.is_active,
      createdAt: data.created_at,
    };
  },

  async update(id: string, updates: Partial<Omit<SecurityStaff, 'id' | 'createdAt'>>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('security_staff')
      .update({
        ...(updates.name !== undefined && { name: updates.name }),
        ...(updates.nationalId !== undefined && { national_id: updates.nationalId }),
        ...(updates.mobile !== undefined && { mobile: updates.mobile }),
        ...(updates.idCardImageUrl !== undefined && { id_card_image_url: updates.idCardImageUrl }),
        ...(updates.isActive !== undefined && { is_active: updates.isActive }),
      })
      .eq('id', id);
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('security_staff').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Security Shifts ──────────────────────────────────────────────────────────
export const securityShiftsService = {
  async getAll(): Promise<SecurityShift[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('security_shifts')
      .select('*, security_staff(name)')
      .order('shift_date', { ascending: false })
      .order('start_time', { ascending: true });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (data || []).map((r) => ({
      id: r.id,
      staffId: r.staff_id,
      staffName: r.security_staff?.name || '',
      shiftType: r.shift_type as ShiftType,
      shiftDate: r.shift_date,
      startTime: r.start_time,
      endTime: r.end_time,
      gate: r.gate || '',
      notes: r.notes || '',
      createdAt: r.created_at,
    }));
  },

  async create(shift: Omit<SecurityShift, 'id' | 'staffName' | 'createdAt'>): Promise<SecurityShift> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('security_shifts')
      .insert({
        staff_id: shift.staffId,
        shift_type: shift.shiftType,
        shift_date: shift.shiftDate,
        start_time: shift.startTime,
        end_time: shift.endTime,
        gate: shift.gate,
        notes: shift.notes,
      })
      .select('*, security_staff(name)')
      .single();
    if (error) throw error;
    return {
      id: data.id,
      staffId: data.staff_id,
      staffName: data.security_staff?.name || '',
      shiftType: data.shift_type as ShiftType,
      shiftDate: data.shift_date,
      startTime: data.start_time,
      endTime: data.end_time,
      gate: data.gate || '',
      notes: data.notes || '',
      createdAt: data.created_at,
    };
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('security_shifts').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── User Profiles (Permissions) ─────────────────────────────────────────────
export const userProfilesService = {
  async getAll(): Promise<UserProfile[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (data || []).map((r) => ({
      id: r.id,
      email: r.email,
      fullName: r.full_name,
      role: r.role as UserRoleLevel,
      department: r.department || '',
      isActive: r.is_active,
      avatarUrl: r.avatar_url || '',
      createdAt: r.created_at,
    }));
  },

  async updateRole(id: string, role: UserRoleLevel): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('user_profiles')
      .update({ role })
      .eq('id', id);
    if (error) throw error;
  },

  async getCurrentUserRole(): Promise<UserRoleLevel> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 'مدخل_بيانات';
    const { data, error } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', user.id)
      .single();
    if (error || !data) return 'مدخل_بيانات';
    return data.role as UserRoleLevel;
  },
};

// ─── Raw Materials ────────────────────────────────────────────────────────────
export const rawMaterialsService = {
  async getAll(): Promise<RawMaterial[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('raw_materials')
      .select('*')
      .order('added_date', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (data || []).map((r) => ({
      id: r.id,
      name: r.name,
      quantity: Number(r.quantity),
      unit: r.unit as MaterialUnit,
      imageUrl: r.image_url || '',
      addedDate: r.added_date,
      notes: r.notes || '',
      createdAt: r.created_at,
    }));
  },

  async create(material: Omit<RawMaterial, 'id' | 'createdAt'>): Promise<RawMaterial> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('raw_materials')
      .insert({
        name: material.name,
        quantity: material.quantity,
        unit: material.unit,
        image_url: material.imageUrl,
        added_date: material.addedDate,
        notes: material.notes,
      })
      .select()
      .single();
    if (error) throw error;
    return {
      id: data.id,
      name: data.name,
      quantity: Number(data.quantity),
      unit: data.unit as MaterialUnit,
      imageUrl: data.image_url || '',
      addedDate: data.added_date,
      notes: data.notes || '',
      createdAt: data.created_at,
    };
  },

  async update(id: string, updates: Partial<Omit<RawMaterial, 'id' | 'createdAt'>>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('raw_materials')
      .update({
        ...(updates.name !== undefined && { name: updates.name }),
        ...(updates.quantity !== undefined && { quantity: updates.quantity }),
        ...(updates.unit !== undefined && { unit: updates.unit }),
        ...(updates.imageUrl !== undefined && { image_url: updates.imageUrl }),
        ...(updates.addedDate !== undefined && { added_date: updates.addedDate }),
        ...(updates.notes !== undefined && { notes: updates.notes }),
      })
      .eq('id', id);
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('raw_materials').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Finished Products ────────────────────────────────────────────────────────
export const finishedProductsService = {
  async getAll(): Promise<FinishedProduct[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('finished_products')
      .select('*')
      .order('production_date', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (data || []).map((r) => ({
      id: r.id,
      name: r.name,
      quantity: Number(r.quantity),
      unit: r.unit as MaterialUnit,
      imageUrl: r.image_url || '',
      productionDate: r.production_date,
      batchNumber: r.batch_number || '',
      notes: r.notes || '',
      createdAt: r.created_at,
    }));
  },

  async create(product: Omit<FinishedProduct, 'id' | 'createdAt'>): Promise<FinishedProduct> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('finished_products')
      .insert({
        name: product.name,
        quantity: product.quantity,
        unit: product.unit,
        image_url: product.imageUrl,
        production_date: product.productionDate,
        batch_number: product.batchNumber,
        notes: product.notes,
      })
      .select()
      .single();
    if (error) throw error;
    return {
      id: data.id,
      name: data.name,
      quantity: Number(data.quantity),
      unit: data.unit as MaterialUnit,
      imageUrl: data.image_url || '',
      productionDate: data.production_date,
      batchNumber: data.batch_number || '',
      notes: data.notes || '',
      createdAt: data.created_at,
    };
  },

  async update(id: string, updates: Partial<Omit<FinishedProduct, 'id' | 'createdAt'>>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('finished_products')
      .update({
        ...(updates.name !== undefined && { name: updates.name }),
        ...(updates.quantity !== undefined && { quantity: updates.quantity }),
        ...(updates.unit !== undefined && { unit: updates.unit }),
        ...(updates.imageUrl !== undefined && { image_url: updates.imageUrl }),
        ...(updates.productionDate !== undefined && { production_date: updates.productionDate }),
        ...(updates.batchNumber !== undefined && { batch_number: updates.batchNumber }),
        ...(updates.notes !== undefined && { notes: updates.notes }),
      })
      .eq('id', id);
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('finished_products').delete().eq('id', id);
    if (error) throw error;
  },
};
